// Um parceiro por navegador. O localStorage é um só para a origem e vários parceiros (ambientes)
// são servidos pelo mesmo deploy, então abrir o link de outro parceiro em outra aba sobrescreve
// ambiente/params/empresa/user da aba aberta e mistura dados em tela (chamado #194664).
// Regras, chaves e cenários: docs/multiplo-web/plugins/parceiroAba.md

export const ROTA_ENCERRADA = '/sessao-encerrada'

const PARCEIRO_ATIVO = 'parceiroAtivo'
const PARCEIRO_ABA = 'parceiroAba'
const PARCEIRO_ABA_ENTRADA = 'parceiroAbaEntrada'
const PARCEIRO_ABA_ENCERRADA = 'parceiroAbaEncerrada'

// Gravadas pelo index.html na URL de entrada antes do boot; sobrevivem à limpeza.
// params só sobrevive se veio do RefreshParametros do parceiro que está entrando.
const CHAVES_PRESERVADAS = ['ambiente', 'appVersion']

const normalizar = (ambiente) => (ambiente ? String(ambiente).trim().toLowerCase() : null)

const lerJson = (chave) => {
  try {
    return JSON.parse(localStorage.getItem(chave))
  } catch {
    return null
  }
}

export function isUrlEntrada ({ pathname, search }) {
  return /\/partner\/[^/]+/.test(pathname) || new URLSearchParams(search).has('p')
}

export function avaliarAba ({ urlEntrada, ambienteAtual, parceiroAtivo, parceiroAba, encerrada }) {
  const atual = normalizar(ambienteAtual)
  const ativo = normalizar(parceiroAtivo)
  const aba = normalizar(parceiroAba)

  if (urlEntrada) {
    return ativo && atual && ativo !== atual ? 'trocar' : 'assumir'
  }
  if (encerrada) {
    return 'encerrada'
  }
  if (aba && ativo && aba !== ativo) {
    return 'encerrada'
  }
  return 'seguir'
}

export function limparStorageParceiro (ambiente) {
  const preservadas = CHAVES_PRESERVADAS
    .map(chave => [chave, localStorage.getItem(chave)])
    .filter(([, valor]) => valor !== null)

  const params = lerJson('params')
  if (params && normalizar(params.ambiente) === normalizar(ambiente)) {
    preservadas.push(['params', localStorage.getItem('params')])
  }

  localStorage.clear()
  preservadas.forEach(([chave, valor]) => localStorage.setItem(chave, valor))
  localStorage.setItem('status', 'loggedOut')
  sessionStorage.clear()
}

// Para logins que só conhecem o ambiente depois de autenticar (authToken, refresh de token).
export function assumirParceiro (ambiente, entrada) {
  const parceiro = normalizar(ambiente)
  if (!parceiro) {
    return
  }
  sessionStorage.setItem(PARCEIRO_ABA, parceiro)
  sessionStorage.setItem(PARCEIRO_ABA_ENTRADA, entrada || `/partner/${ambiente}`)
  localStorage.setItem(PARCEIRO_ATIVO, parceiro)
}

export function encerrarAba () {
  sessionStorage.setItem(PARCEIRO_ABA_ENCERRADA, 'true')
  const targetWindow = window.parent ?? window.top ?? window.opener ?? window
  targetWindow.postMessage('multiplo.sessaoEncerrada', '*')
  location.replace(ROTA_ENCERRADA)
}

export function observarOutrasAbas () {
  window.addEventListener('storage', (e) => {
    // localStorage.clear() (logout/inatividade) chega com key null e não encerra ninguém.
    if (e.key !== PARCEIRO_ATIVO || !e.newValue) {
      return
    }
    const aba = sessionStorage.getItem(PARCEIRO_ABA)
    if (aba && aba !== normalizar(e.newValue)) {
      encerrarAba()
    }
  })
}

// HTML puro e sem marca de parceiro: o Vue não sobe, nada de tema, logo ou API.
export function renderizarTelaEncerrada () {
  const app = document.getElementById('app') || document.body
  app.style.display = 'block'
  app.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;box-sizing:border-box;background:#fff;font-family:Roboto,Arial,sans-serif;">
      <div style="max-width:420px;text-align:center;color:#3E3E3E;">
        <p style="font-size:20px;font-weight:700;margin:0 0 12px;">Sessão encerrada</p>
        <p style="font-size:15px;line-height:22px;margin:0 0 24px;">
          Você acessou outro parceiro em outra aba deste navegador. Por segurança, esta sessão foi encerrada.
        </p>
        <button id="parceiro-aba-entrar" type="button" style="background:#3E3E3E;color:#fff;border:0;border-radius:24px;padding:12px 32px;font-size:15px;cursor:pointer;">
          Entrar novamente
        </button>
      </div>
    </div>`

  document.getElementById('parceiro-aba-entrar').addEventListener('click', () => {
    const entrada = sessionStorage.getItem(PARCEIRO_ABA_ENTRADA) || '/'
    sessionStorage.removeItem(PARCEIRO_ABA_ENCERRADA)
    sessionStorage.removeItem(PARCEIRO_ABA)
    location.href = entrada
  })
}

// Chamado no topo do main.js, antes do store do @bmp-vue/core ler o localStorage.
// Retorna false quando a aba foi encerrada e o app não deve subir.
export function iniciarParceiroAba (ambientePadrao) {
  const urlEntrada = isUrlEntrada(location)
  const ambienteAtual = localStorage.getItem('ambiente') || ambientePadrao
  // Na URL de entrada sem parceiroAtivo (primeiro acesso após o deploy), o dono do storage é a
  // empresa já carregada. Fora dela não se usa esse fallback, para não encerrar a aba por engano.
  const parceiroAtivo = localStorage.getItem(PARCEIRO_ATIVO) || (urlEntrada ? lerJson('empresa')?.ambiente : null)
  const acao = avaliarAba({
    urlEntrada,
    ambienteAtual,
    parceiroAtivo,
    parceiroAba: sessionStorage.getItem(PARCEIRO_ABA),
    encerrada: sessionStorage.getItem(PARCEIRO_ABA_ENCERRADA) === 'true'
  })

  if (acao === 'encerrada') {
    renderizarTelaEncerrada()
    return false
  }

  if (acao === 'trocar') {
    limparStorageParceiro(ambienteAtual)
  }

  if (acao === 'trocar' || acao === 'assumir') {
    sessionStorage.removeItem(PARCEIRO_ABA_ENCERRADA)
    assumirParceiro(ambienteAtual, location.pathname + location.search)
  } else {
    const parceiro = sessionStorage.getItem(PARCEIRO_ABA) || localStorage.getItem(PARCEIRO_ATIVO) || normalizar(ambienteAtual)
    if (parceiro) {
      sessionStorage.setItem(PARCEIRO_ABA, parceiro)
      if (!localStorage.getItem(PARCEIRO_ATIVO)) {
        localStorage.setItem(PARCEIRO_ATIVO, parceiro)
      }
    }
  }

  observarOutrasAbas()
  return true
}
